"use client";

import { useEffect, useState } from "react";
import API from "../utils/api";
import "./PurchaseReport.css";
import { format } from "date-fns";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PurchaseReport = () => {
  const [activeTab, setActiveTab] = useState("today");
  const [fromDate, setFromDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingCSV, setGeneratingCSV] = useState(false);
  const [groupedRows, setGroupedRows] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    // Load today's purchases on mount and when switching back to Today
    if (activeTab === "today") {
      const today = format(new Date(), "yyyy-MM-dd");
      fetchByRange(today, today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const groupByDateAndCompany = (items = []) => {
    const groupsMap = new Map();
    for (const p of items) {
      const dateStr = p.date ? format(new Date(p.date), "yyyy-MM-dd") : "N/A";
      const company = p.supplierName || p.customerName || "Unknown";
      const key = `${dateStr}::${company}`;
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          date: dateStr,
          companyName: company,
          items: [],
          totalQuantity: 0,
        });
      }
      const g = groupsMap.get(key);
      g.items.push(p);
      g.totalQuantity += Number(p.quantity) || 0;
    }
    return Array.from(groupsMap.values());
  };

  const fetchByRange = async (start, end) => {
    setLoading(true);
    setMessage("");
    try {
      const res = await API.get(`/purchases/report?start=${start}&end=${end}`);
      const items = res.data?.purchases || res.data || [];
      setGroupedRows(groupByDateAndCompany(items));
    } catch (err) {
      console.error("Error fetching purchases by range:", err);
      setGroupedRows([]);
      setMessage("❌ Failed to fetch purchase report");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    setGeneratingPDF(true);
    
    try {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Purchase Report", 105, 15, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(`Generated on: ${format(new Date(), "yyyy-MM-dd HH:mm")}`, 105, 22, { align: "center" });

      const tableData = groupedRows.map((g) => [
        g.date,
        g.companyName,
        g.totalQuantity.toString(),
      ]);

      autoTable(doc, {
        head: [['Date', 'Company', 'Quantity']],
        body: tableData,
        startY: 25,
        margin: { top: 25 },
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [22, 160, 133],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 80 },
          2: { cellWidth: 40, halign: 'right' },
        }
      });

      doc.save(`purchase-report-${format(new Date(), "yyyy-MM-dd")}.pdf`);
      setMessage("✅ PDF downloaded successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      setMessage("❌ Failed to generate PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  const downloadCSV = () => {
    if (!groupedRows || groupedRows.length === 0) return;
    setGeneratingCSV(true);
    try {
      const header = ["Date", "Company", "Quantity"];
      const rows = groupedRows.map((g) => [g.date, g.companyName, g.totalQuantity]);
      const csv = [header, ...rows]
        .map((r) => r.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `purchase-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setMessage("✅ CSV downloaded successfully");
    } catch (e) {
      setMessage("❌ Failed to generate CSV");
    } finally {
      setGeneratingCSV(false);
    }
  };

  return (
    <div className="purchase-report-container">
      <div className="report-header">
        <h2>
          <i className="fas fa-file-invoice"></i> Purchase Report
        </h2>
        <div className="download-actions">
          <button 
            onClick={downloadPDF} 
            disabled={generatingPDF || groupedRows.length === 0}
            className="download-pdf-btn"
          >
            {generatingPDF ? (
              <><i className="fas fa-spinner fa-spin"></i> Generating PDF...</>
            ) : (
              <><i className="fas fa-download"></i> Download PDF</>
            )}
          </button>
          <button 
            onClick={downloadCSV}
            disabled={generatingCSV || groupedRows.length === 0}
            className="download-csv-btn"
          >
            {generatingCSV ? (
              <><i className="fas fa-spinner fa-spin"></i> Generating...</>
            ) : (
              <><i className="fas fa-file-excel"></i> Download Excel</>
            )}
          </button>
        </div>
      </div>

      <div className="report-controls">
        <div className="tabs">
          <button className={`tab-btn ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>Today</button>
          <button className={`tab-btn ${activeTab === 'range' ? 'active' : ''}`} onClick={() => setActiveTab('range')}>From–To</button>
        </div>
        <div className="range-controls">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <span className="separator">to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <button className="apply-btn" onClick={() => fetchByRange(fromDate, toDate)}>
            Apply
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading report...</p>
      ) : groupedRows.length === 0 ? (
        <p>No purchase records found.</p>
      ) : selectedGroup ? (
        <div className="report-details">
          <button className="back-btn" onClick={() => setSelectedGroup(null)}>
            ← Back to Purchases
          </button>
          <h3>Company: {selectedGroup.companyName} — {selectedGroup.date}</h3>
          <table className="report-table">
            <thead>
              <tr>
                <th>Size</th>
                <th>Model</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {selectedGroup.items.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.size || item.product?.size || '-'}</td>
                  <td>{item.modelNo || item.product?.modelNo || '-'}</td>
                  <td>{item.quantity}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Total</td>
                <td>{selectedGroup.totalQuantity}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <table className="report-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Company Name</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {groupedRows.map((row) => (
              <tr key={row.key} className="clickable-row" onClick={() => setSelectedGroup(row)}>
                <td>{row.date}</td>
                <td>{row.companyName}</td>
                <td>{row.totalQuantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {message && <p className="report-message">{message}</p>}
    </div>
  );
};

export default PurchaseReport;
